'use client'

import { useEffect } from 'react'
import { FileText } from 'lucide-react'

import { splitFileName } from '@/lib/upload/available-file-name'
import { useNameConflictPromptStore } from '@/lib/upload/name-conflict-prompt-store'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { FileTypeIcon } from '@/components/misc/file-type-icon'

const KNOWN_ICON_TYPES = new Set(['pdf', 'doc', 'docx', 'xls', 'xlsx'])

/**
 * "Duplicate file detected" prompt. Mounted once at the root; any upload that
 * collides by name with a document in the same library waits here for the
 * user to cancel, keep both, or replace the existing file.
 */
export function DuplicateFileDialog() {
  const registerHost = useNameConflictPromptStore((state) => state.registerHost)
  const current = useNameConflictPromptStore((state) => state.queue[0])
  const remaining = useNameConflictPromptStore((state) => state.queue.length)
  const answer = useNameConflictPromptStore((state) => state.answer)

  useEffect(() => registerHost(), [registerHost])

  if (!current) return null

  const { id, conflict } = current
  const extension = splitFileName(conflict.fileName)
    .extension.replace(/^\./, '')
    .toLowerCase()

  return (
    <Dialog
      // Re-key per prompt so each queued file opens fresh.
      key={id}
      open
      onOpenChange={(open) => {
        if (!open) answer(id, 'cancel')
      }}
    >
      <DialogContent className="gap-5 rounded-3xl p-6 sm:max-w-lg">
        <DialogHeader className="gap-3">
          <DialogTitle className="text-lg font-semibold">
            Duplicate file detected
          </DialogTitle>
          <DialogDescription>
            A file with this name already exists here. Replacing it removes the
            existing file from your library.
          </DialogDescription>
        </DialogHeader>

        <div className="bg-muted/60 flex items-center gap-3 rounded-2xl p-4">
          <div className="bg-background flex size-10 shrink-0 items-center justify-center rounded-lg shadow-xs">
            {KNOWN_ICON_TYPES.has(extension) ? (
              <FileTypeIcon fileType={extension} iconSize="size-6" />
            ) : (
              <FileText className="text-muted-foreground size-5" />
            )}
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium">{conflict.existingName}</p>
            <p className="text-muted-foreground truncate text-xs">
              Available name: {conflict.availableName}
            </p>
          </div>
        </div>

        {remaining > 1 ? (
          <p className="text-muted-foreground -mt-2 text-xs">
            {remaining - 1} more duplicate{remaining === 2 ? '' : 's'} waiting
          </p>
        ) : null}

        <div className="grid gap-2 sm:grid-cols-3">
          <Button
            variant="secondary"
            size="lg"
            className="rounded-full"
            onClick={() => answer(id, 'cancel')}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            size="lg"
            className="rounded-full"
            onClick={() => answer(id, 'keep_both')}
          >
            Keep both files
          </Button>
          <Button
            size="lg"
            className="rounded-full"
            onClick={() => answer(id, 'replace')}
          >
            Replace existing file
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
